import { Error, Layout, LayoutProps, useCheckForApplicationUpdate, useLoading } from "react-admin";
import * as Sentry from "@sentry/react";
import { ReactQueryDevtools } from "react-query/devtools";
import { BackofficeAppBar } from "./BackofficeAppBar";
import { BackofficeSideBar } from "./BackofficeSideBar";
import { useLocation } from "react-router-dom";
import { CircularProgress, useTheme } from "@mui/material";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { backofficeBrandForDrivingSchool } from "./backofficeTheme";
import { MinAppVersionNotification } from "./MinAppVersionNotification";
import { useInitGlobalNamespace } from "../hooks/useInitGlobalNamespace";
import { useEffect } from "react";
import { BackofficeGlobalStyles } from "./BackofficeGlobalStyles";

export const BackofficeLayout = ({ children, error: errorComponent, className, ...rest }: LayoutProps) => {
  useCheckForApplicationUpdate({
    onNewVersionAvailable: () => {
      // Enforce update on next navigation ...
      // eslint-disable-next-line react-compiler/react-compiler
      window.onNavigation = () => location.reload();
    },
  });
  useInitGlobalNamespace();
  const [{ drivingSchoolId }] = useAutovioContext();
  const { pathname } = useLocation();
  const theme = useTheme();
  const appBar: any = BackofficeAppBar;
  const sidebar: any = BackofficeSideBar;
  if (className) {
    className += " new-design";
  } else {
    className = "new-design";
  }
  const brand = backofficeBrandForDrivingSchool(drivingSchoolId);
  useEffect(() => {
    // Side effect: set CSS class of <body> element (for example "autovioTheme" or "academyTheme") ,,,
    document.body.className = `${brand}Theme`;
    // Side effect: update color-scheme CSS property of  the <html> element (either "light" or "dark") ,,,
    document.documentElement.style.colorScheme = theme.palette.mode;
  }, [brand, theme.palette.mode]);
  return (
    <>
      <BackofficeGlobalStyles />
      <MinAppVersionNotification />
      <Layout className={className} {...rest} appBar={appBar} sidebar={sidebar} appBarAlwaysOn>
        {pathname !== "/" && (
          <div id="react-admin-title">
            {/* this is a React portal, usually filled by the BackofficeListTitle or BackofficeRecordTitle component */}
          </div>
        )}
        <Sentry.ErrorBoundary
          // eslint-disable-next-line @typescript-eslint/unbound-method
          fallback={({ error, componentStack, resetError }) => (
            <Error
              error={error as Error}
              errorComponent={errorComponent}
              errorInfo={{ componentStack }}
              resetErrorBoundary={resetError}
            />
          )}
        >
          {children}
        </Sentry.ErrorBoundary>
      </Layout>
      {location.hostname === "localhost" && !location.search.includes("visualTest") && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
      <LoadingIndicator />
    </>
  );
};

/**
 * Side effects:
 * - update window.loading to reflect the current loading state,
 * - update window.loadingCounter whenever the loading state changes from false to true.
 */
function LoadingIndicator() {
  const loading = useLoading();
  if (!loading) {
    // eslint-disable-next-line react-compiler/react-compiler
    window.loading = false;
    return null;
  }
  if (!window.loading) {
    window.loading = true;
    window.loadingCounter = (window.loadingCounter ?? 0) + 1;
  }
  return (
    <div style={{ position: "fixed", left: "12px", bottom: "6px", zIndex: 999999 }}>
      <CircularProgress id="LoadingIndicator" color="inherit" size="40px" thickness={5} />
    </div>
  );
}
